import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { getOnlineModelForBasketItem } from '../../ShoppingBasket/shoppingBasketUtils.js';
import { fetchPrereserveNumbers } from '../utils/subscriptionConfigurationApi.js';
import { isVoiceSubscription } from '../utils/subscriptionConfigurationUtils.js';
import type { ShoppingBasketType } from '../../../common/types/shoppingBasket.js';
import type { OnlineModel } from '../../../generated/api/onlineModel.js';
import type { PlaceOrderFormData } from '../placeOrderTypes.js';

/** Maximum number of phone numbers to prereserve */
const MAX_PHONE_NUMBERS = 30;

export interface UsePhoneNumbersOptions {
  mdmId: string;
  shoppingBasket?: ShoppingBasketType;
  onlineModels?: OnlineModel[];
}

export interface UsePhoneNumbersResult {
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => Promise<void>;
}

/**
 * Hook to manage phone numbers for voice subscriptions in PlaceOrder.
 * Handles:
 * - Loading initial phone numbers from API
 * - Preserving selected numbers when navigating back
 * - Loading more numbers on demand
 */
export const usePhoneNumbers = ({
  shoppingBasket,
  onlineModels,
  mdmId,
}: UsePhoneNumbersOptions): UsePhoneNumbersResult => {
  const methods = useFormContext<PlaceOrderFormData>();

  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);

  // Track the last voice subscription count we fetched for to avoid redundant fetches
  const lastFetchedForCount = useRef(0);

  // Get already selected phone numbers from form state (for back navigation)
  const watchedOrderItemUnits = methods.watch('products.placeOrderItemUnits') || [];
  const selectedPhoneNumbers = watchedOrderItemUnits
    .map(unit => unit.mobileNumber)
    .filter((num): num is string => !!num);

  // Count total voice subscription units in the basket
  const voiceSubscriptionCount =
    shoppingBasket?.items?.reduce((count, item) => {
      const onlineModel = getOnlineModelForBasketItem(onlineModels ?? [], item);
      return onlineModel && isVoiceSubscription(onlineModel) ? count + (item.quantity ?? 1) : count;
    }, 0) ?? 0;

  // Load phone numbers for voice subscriptions — runs on initial load and when quantity increases
  useEffect(() => {
    const loadPhoneNumbers = async () => {
      if (voiceSubscriptionCount === 0) {
        return;
      }

      const targetCount = Math.min(Math.max(voiceSubscriptionCount, 6), MAX_PHONE_NUMBERS);

      // Skip if we already fetched enough for this count
      if (lastFetchedForCount.current >= targetCount) {
        return;
      }

      lastFetchedForCount.current = targetCount;

      setPhoneNumbersLoading(true);
      try {
        // Start with already-loaded numbers (includes selected from back navigation)
        const existingNumbers = phoneNumbers.length > 0 ? [...phoneNumbers] : [...selectedPhoneNumbers];

        // Ensure any selected numbers missing from the pool are included
        const missingSelected = selectedPhoneNumbers.filter(n => !existingNumbers.includes(n));
        let combinedNumbers = [...missingSelected, ...existingNumbers];

        // Fetch in a loop — the API may return fewer numbers than requested per call
        while (combinedNumbers.length < targetCount) {
          const remaining = targetCount - combinedNumbers.length;
          const newNumbers = await fetchPrereserveNumbers(mdmId, remaining);
          const uniqueNew = newNumbers.filter(n => !combinedNumbers.includes(n));
          if (uniqueNew.length === 0) {
            break; // No more numbers available from the API
          }
          combinedNumbers = [...combinedNumbers, ...uniqueNew];
        }
        setPhoneNumbers(combinedNumbers);
      } catch (error) {
        console.warn('Failed to prereserve phone numbers:', error);
      } finally {
        setPhoneNumbersLoading(false);
      }
    };

    loadPhoneNumbers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceSubscriptionCount, mdmId]);

  // Load more phone numbers handler
  const onLoadMoreNumbers = useCallback(async () => {
    if (phoneNumbersLoading || phoneNumbers.length >= MAX_PHONE_NUMBERS) {
      return;
    }

    setPhoneNumbersLoading(true);
    try {
      const count = Math.min(6, MAX_PHONE_NUMBERS - phoneNumbers.length);
      const newNumbers = await fetchPrereserveNumbers(mdmId, count);
      setPhoneNumbers(prev => [...prev, ...newNumbers]);
    } catch (error) {
      console.warn('Failed to load more phone numbers:', error);
    } finally {
      setPhoneNumbersLoading(false);
    }
  }, [phoneNumbers.length, phoneNumbersLoading, mdmId]);

  return {
    phoneNumbers,
    phoneNumbersLoading,
    onLoadMoreNumbers,
  };
};
